import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { apiGet, apiPost, markTokenFresh } from "../utils/api";
import { ApiError } from "../utils/errors";
import { COOKIE_CSRF, LEGACY_COOKIE_CSRF } from "../utils/cookieNames";

/**
 * Wipe the (non-HttpOnly) csrf cookie so the mount-time short-circuit
 * below doesn't re-authenticate a freshly-logged-out user. Clears both the
 * host-only and parent-domain scopes, and both the current `dhze_csrf_token`
 * and the legacy `csrf-token` — historical deployments have set cookies on
 * either, and we want the slate clean.
 */
function clearCsrfCookieClientSide(): void {
  const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  document.cookie = `${COOKIE_CSRF}=; ${expire}`;
  document.cookie = `${LEGACY_COOKIE_CSRF}=; ${expire}`;
  const parts = window.location.hostname.split(".");
  if (parts.length >= 2) {
    const parentDomain = parts.slice(-2).join(".");
    document.cookie = `${COOKIE_CSRF}=; ${expire}; domain=.${parentDomain}`;
    document.cookie = `${LEGACY_COOKIE_CSRF}=; ${expire}; domain=.${parentDomain}`;
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  twoFactorEnabled: boolean;
  emailVerified: boolean;
  partnerId: string | null;
  // Rol binnen de partner-organisatie (alleen relevant bij partnerId gezet).
  // null/undefined = account van vóór de rol-differentiatie → SERVICEMANAGER.
  partnerRole?: "MONTEUR" | "SERVICEMANAGER" | null;
  phoneNumber?: string | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  verify2FABackup: (tempToken: string, backupCode: string) => Promise<void>;
}

interface LoginResult {
  twoFactorRequired?: boolean;
  tempToken?: string;
  userId?: string;
}

interface LoginPayload {
  user?: User;
  twoFactorRequired?: boolean;
  tempToken?: string;
  userId?: string;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // ── Check auth on mount ──
  // Quick short-circuit: csrf cookie is set alongside auth cookies and is
  // readable from JS. No cookie ⇒ definitely no session ⇒ skip the /auth/me
  // round-trip (and the noisy 401 it would produce after a logout / DB-wipe).
  const refreshAuth = useCallback(async () => {
    if (!document.cookie.includes(`${COOKIE_CSRF}=`)) {
      setState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    try {
      const me = await apiGet<User>("/auth/me");
      markTokenFresh();
      setState({ user: me.data!, isAuthenticated: true, isLoading: false });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        try {
          await apiPost("/auth/refresh");
          const me = await apiGet<User>("/auth/me");
          markTokenFresh();
          setState({ user: me.data!, isAuthenticated: true, isLoading: false });
          return;
        } catch {
          // Refresh also failed — server has wiped the session (or cookies
          // are stale from a deploy / DB-wipe). The api wrapper already
          // redirected to /login on the failed refresh; just settle local
          // state so ProtectedRoute doesn't flash the loading screen.
        }
      }
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // ── Silent token refresh (every 13 minutes, before the 15min expiry) ──
  useEffect(() => {
    if (!state.isAuthenticated) return;

    let consecutiveFailures = 0;
    const MAX_FAILURES = 3;

    const interval = setInterval(
      async () => {
        try {
          await apiPost("/auth/refresh");
          markTokenFresh();
          consecutiveFailures = 0;
        } catch {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_FAILURES) {
            setState({ user: null, isAuthenticated: false, isLoading: false });
          }
        }
      },
      13 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [state.isAuthenticated]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await apiPost<LoginPayload>("/auth/login", {
        email,
        password,
      });
      const data = res.data!;

      if (data.twoFactorRequired) {
        return {
          twoFactorRequired: true,
          tempToken: data.tempToken,
          userId: data.userId,
        };
      }

      if (data.user) {
        markTokenFresh();
        setState({ user: data.user, isAuthenticated: true, isLoading: false });
      }

      return {};
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    // Wipe the JS-readable csrf-token cookie *first* so a racing background
    // request can't re-mount AuthProvider and re-authenticate via the
    // mount-time short-circuit before /auth/logout's response lands.
    clearCsrfCookieClientSide();
    try {
      await apiPost("/auth/logout");
    } catch {
      // Continue even if server call fails — local state is the source of truth
    }
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const verify2FA = useCallback(
    async (tempToken: string, code: string): Promise<void> => {
      const res = await apiPost<{ user: User }>("/auth/verify-2fa", {
        tempToken,
        code,
      });
      markTokenFresh();
      setState({
        user: res.data!.user,
        isAuthenticated: true,
        isLoading: false,
      });
    },
    [],
  );

  const verify2FABackup = useCallback(
    async (tempToken: string, backupCode: string): Promise<void> => {
      const res = await apiPost<{ user: User }>("/auth/verify-2fa-backup", {
        tempToken,
        backupCode,
      });
      markTokenFresh();
      setState({
        user: res.data!.user,
        isAuthenticated: true,
        isLoading: false,
      });
    },
    [],
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshAuth,
    verify2FA,
    verify2FABackup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
